import type { Plugin, PluginSignature } from "@/types/api";

export interface NormalizedPluginSignature {
  signed: boolean;
  verified: boolean;
  publisher: string | null;
  keyId: string | null;
}

export interface NormalizedPlugin {
  id: string;
  name: string;
  version: string;
  description: string | null;
  enabled: boolean;
  status: string;
  source: "builtin" | "manifest";
  runtimeAttached: boolean;
  contracts: string[];
  permissions: string[];
  apiBase: string;
  openapiUrl: string;
  signature: NormalizedPluginSignature | null;
  valid: boolean;
}

export function normalizePlugin(plugin: Plugin): NormalizedPlugin {
  const attributes: Partial<Plugin["attributes"]> = plugin.attributes ?? {};
  const id = typeof plugin.id === "string" && plugin.id ? plugin.id : "invalid_plugin";
  const name = typeof attributes.name === "string" && attributes.name ? attributes.name : "Invalid plugin";
  const version = typeof attributes.version === "string" && attributes.version ? attributes.version : "-";
  const status = typeof attributes.status === "string" && attributes.status ? attributes.status : "unhealthy";
  const source = attributes.source === "manifest" ? "manifest" : "builtin";
  const apiBase = typeof attributes.api_base === "string" ? attributes.api_base : `/api/v1/plugins/${id}`;
  const openapiUrl = typeof attributes.openapi_url === "string" ? attributes.openapi_url : `${apiBase}/openapi`;
  const valid = Boolean(plugin.id && plugin.attributes && attributes.name && attributes.version);
  return {
    id,
    name,
    version,
    description: typeof attributes.description === "string" ? attributes.description : null,
    enabled: attributes.enabled === true,
    status,
    source,
    runtimeAttached: attributes.runtime_attached === true,
    contracts: stringList(attributes.contracts),
    permissions: stringList(attributes.permissions),
    apiBase,
    openapiUrl,
    signature: normalizeSignature(attributes.signature),
    valid,
  };
}

export interface PluginTrustBadge {
  label: string;
  kind: "core" | "verified" | "community";
}

/**
 * Trust badge for a plugin. Built-ins are core-trusted (shipped in KnotFin);
 * manifest plugins are "Verified publisher" (signed by a trusted key) or
 * "Community" (unsigned).
 */
export function pluginTrustBadge(plugin: NormalizedPlugin): PluginTrustBadge {
  if (plugin.source === "builtin") {
    return { label: "Core", kind: "core" };
  }
  if (plugin.signature?.verified) {
    return {
      label: plugin.signature.publisher
        ? `Verified publisher: ${plugin.signature.publisher}`
        : "Verified publisher",
      kind: "verified",
    };
  }
  return { label: "Community", kind: "community" };
}

function normalizeSignature(sig: PluginSignature | undefined): NormalizedPluginSignature | null {
  if (!sig || typeof sig !== "object") {
    return null;
  }
  return {
    signed: sig.signed === true,
    verified: sig.verified === true,
    publisher: typeof sig.publisher === "string" && sig.publisher ? sig.publisher : null,
    keyId: typeof sig.key_id === "string" && sig.key_id ? sig.key_id : null,
  };
}

export function pluginRuntimeLabel(plugin: NormalizedPlugin): string {
  if (plugin.source === "builtin") {
    return "Managed by KnotFin";
  }
  if (plugin.runtimeAttached) {
    return "External service";
  }
  return "Manifest only";
}

export function pluginSourceLabel(plugin: NormalizedPlugin): string {
  return plugin.source === "builtin" ? "Built in" : "Installed";
}

export function formatPluginList(values: string[]): string {
  return values.length > 0 ? values.join(", ") : "None";
}

function stringList(values: string[] | null | undefined): string[] {
  return Array.isArray(values) ? values : [];
}
